import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux'

import "./index.css"
import '../styles/style.css'

import _ from "lodash";
import Fuse from 'fuse.js';
import { InputText } from "primereact/inputtext";
import { Dialog } from 'primereact/dialog';
import { DataView } from 'primereact/dataview';

import Product from "./Components/Product";
import Checkout from "./Components/Checkout";
import Loading from "../../../common/Loading";

import { updateConsumerCart } from "../../../services/api/consumerCart";
import { getConsumerCartItemsByUserId } from "../../../services/api/consumerCartItem";
import { getConsumerProducts, placeConsumerOrder } from "../../../services/api/poPunch";
import { getCompanyBranchShipAddrs } from "../../../services/api/companyBranchShipAddr";

import { raiseToast } from "../../../services/redux/reducers/toast/toast";
import { fetchMaterialBrands, fetchMaterialCategories, fetchMaterialUoms } from "../../../services/redux/reducers/commonThunk";

import { round, sortArrayOfObjectsInAlphabeticalOrder } from "../../../utils";
import ProductItemSkeleton from "../../../skeletons/ProductItem";
import { Skeleton } from "primereact/skeleton";


const ConsumerRequest = () => {

    const [showMore, setShowMore] = useState(false)
    const [isLoaderVisible, setIsLoaderVisible] = useState(true)
    const [visibleCheckout, setVisibleCheckout] = useState(false)

    const [selectedShipAddrId, setSelectedShipAddrId] = useState(null)

    const [searchText, setSearchText] = useState('')
    const [searchBrandText, setSearchBrandText] = useState('')
    const [searchCategoryText, setSearchCategoryText] = useState('')

    const [brandToCategoryMap, setBrandToCategoryMap] = useState({})
    const [categoryToBrandMap, setCategoryToBrandMap] = useState({})

    const [products, setProducts] = useState([])
    const [cartItems, setCartItems] = useState([])
    const [brands, setBrands] = useState([])
    const [categories, setCategories] = useState([])

    const [shippingAddresses, setShippingAddresses] = useState([])
    const [remarks, setRemarks] = useState("")
    const [selectedProducts, setSelectedProducts] = useState([])
    const [selectedBrandIds, setSelectedBrandIds] = useState([])
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([])

    const dispatch = useDispatch()

    const userDetails = useSelector((state) => state.userDetails)
    const materialBrands = useSelector((state) => state.common.materialBrands)
    const materialCategories = useSelector((state) => state.common.materialCategories)
    const materialUOMs = useSelector((state) => state.common.materialUOMs)

    const currentUserRole = userDetails?.activeUserRoleDetails

    useEffect(() => {
        dispatch(fetchMaterialBrands())
        dispatch(fetchMaterialCategories())
        dispatch(fetchMaterialUoms())
        fetchShippingAddresses()
        fetchConsumerCartItems()
    }, [])

    useEffect(() => {
        handleBrandFuzzySearch()
    }, [searchBrandText])

    useEffect(() => {
        handleCategoryFuzzySearch()
    }, [searchCategoryText])

    useEffect(() => {
        if(materialCategories.length && materialUOMs.length && currentUserRole){
            if(currentUserRole.branchId){
                fetchConsumerProducts()
            } else {
                dispatch(raiseToast({ severity: 'error', detail: 'Use branch level role', life: 3000 }))
            }
        }
    }, [materialCategories, materialUOMs, currentUserRole])

    useEffect(() => {
        handleFuzzySearch()
    }, [searchText, selectedCategoryIds, products, selectedBrandIds])

    const handleFuzzySearch = () => {
        let filteredProducts = products
        if(selectedCategoryIds.length > 0){
            filteredProducts = products.filter((product) => selectedCategoryIds.includes(product.categoryId))
        } else {
            filteredProducts = products
        }

        if(selectedBrandIds.length > 0){
            filteredProducts = filteredProducts.filter((product) => selectedBrandIds.includes(product.brandId))
        } else {
            filteredProducts = filteredProducts
        }

        if(searchText.length > 0){
            const fuseRC = new Fuse(filteredProducts, {
                keys: ['productString'],
                includeScore: true,
                threshold: 0.3,
                ignoreLocation: true,
                shouldSort: true,
                getFn: (obj) => {
                    return obj.productString.replace(/\s+/g, '')
                }
            })

            const searchResults = fuseRC.search(searchText)
            const relatedProducts = searchResults.map((result) => result.item)
            setSelectedProducts(relatedProducts)
        } else {
            setSelectedProducts(filteredProducts)
        }
    }

    const handleBrandFuzzySearch = () => {
        const brandIds = _.uniqBy(products, "brandId").map(obj => obj.brandId)
        let relatedBrands = []
        materialBrands.map(eachBrand => {
            if (brandIds.includes(eachBrand.brandId)) {
                relatedBrands.push(eachBrand)
            }
        })
        relatedBrands = sortArrayOfObjectsInAlphabeticalOrder(relatedBrands, "brandName")

        if(searchBrandText.length > 0){
            const fuseBrands = new Fuse(relatedBrands, {
                keys: ['brandName'],
                includeScore: true,
                threshold: 0.3,
                ignoreLocation: true,
                shouldSort: true,
                getFn: (obj) => {
                    return obj.brandName.replace(/\s+/g, '')
                }
            })

            const searchResults = fuseBrands.search(searchBrandText)
            const relatedBrandResults = searchResults.map((result) => result.item)
            setBrands(relatedBrandResults)
        } else {
            setBrands(relatedBrands)
        }
    }

    const handleCategoryFuzzySearch = () => {
        const categoryIds = _.uniqBy(products, "categoryId").map(obj => obj.categoryId)
        let relatedCategories = []
        materialCategories.map(eachCategory => {
            if (categoryIds.includes(eachCategory.categoryId)) {
                relatedCategories.push(eachCategory)
            }
        })
        relatedCategories = sortArrayOfObjectsInAlphabeticalOrder(relatedCategories, "categoryName")

        if(searchCategoryText.length > 0){
            const fuseCategories = new Fuse(relatedCategories, {
                keys: ['categoryName'],
                includeScore: true,
                threshold: 0.3,
                ignoreLocation: true,
                shouldSort: true,
                getFn: (obj) => {
                    return obj.categoryName.replace(/\s+/g, '')
                }
            })

            const searchResults = fuseCategories.search(searchCategoryText)
            const relatedCategoryResults = searchResults.map((result) => result.item)
            setCategories(relatedCategoryResults)
        } else {
            setCategories(relatedCategories)
        }
    }

    const handleSelectCategory = (categoryId) => {
        const isSelected = selectedCategoryIds.includes(categoryId)

        if (isSelected) {
            setSelectedCategoryIds(prevCategories => prevCategories.filter(id => id !== categoryId))
        } else {
            setSelectedCategoryIds(prevCategories => [...prevCategories, categoryId])
        }
    }

    const handleSelectBrand = (brandId) => {
        const isSelected = selectedBrandIds.includes(brandId)

        if (isSelected) {
            setSelectedBrandIds(prevBrands => prevBrands.filter(id => id !== brandId))
        } else {
            setSelectedBrandIds(prevBrands => [...prevBrands, brandId])
        }
    }

    const fetchConsumerProducts = async () => {
        const response = await getConsumerProducts(currentUserRole.branchId)
        // console.log("fetchConsumerProducts", response)

        if (response.failure) {
            dispatch(raiseToast({ severity: 'error', detail: response.data.error, life: 3000 }))
        } else {
            const categoryIds = _.uniqBy(response.data, "categoryId").map(obj => obj.categoryId)
            const brandIds = _.uniqBy(response.data, "brandId").map(obj => obj.brandId)

            const modifiedConsumerItems = response.data.map(eachProduct => {
                const erpCode = eachProduct.companyERPCode ? `${eachProduct.companyERPCode} |` : ''
                const productName = eachProduct.companyProdName ? eachProduct.companyProdName : eachProduct.productName
                const productSpecification = eachProduct.companyProdSpec ? eachProduct.companyProdSpec : eachProduct?.productSpecification
                const productString = `${eachProduct?.materialId} | ${erpCode} | ${productName} | ${productSpecification} | ${eachProduct?.docNo} | ${eachProduct?.clientPONum} }`

                const uomName = materialUOMs?.find(eachUOM => {
                    return eachUOM.UOM === eachProduct.companyERPUOM
                })

                const relatedMaterialCategory = materialCategories?.find(eachCategory => {
                    return eachCategory.categoryId === eachProduct.categoryId
                })

                const availableQty = eachProduct.companyQtyMultiplier ? round(eachProduct.availableQty / eachProduct.companyQtyMultiplier) : eachProduct.availableQty
                const bPOQty = eachProduct.companyQtyMultiplier ? round(eachProduct.bPOQty / eachProduct.companyQtyMultiplier) : eachProduct.bPOQty

                return { ...eachProduct, productString, materialUOMId: uomName?.materialUOMId, categoryName: relatedMaterialCategory?.categoryName, availableQty: availableQty, bPOQty: bPOQty }
            })
            const availableItems = modifiedConsumerItems.filter(eachConsumerItem => eachConsumerItem.availableQty > 0)
            const exhaustedItems = modifiedConsumerItems.filter(eachConsumerItem => eachConsumerItem.availableQty === 0)
            // console.log("modifiedConsumerItems", modifiedConsumerItems)
            setSelectedProducts([...availableItems, ...exhaustedItems])
            setProducts([...availableItems, ...exhaustedItems])

            let categoryToBrandMap = {}
            let brandToCategoryMap = {}

            let relatedCategories = []
            materialCategories.map(eachCategory => {
                if (categoryIds.includes(eachCategory.categoryId)) {
                    let relatedBrandIds = []
                    modifiedConsumerItems.map(eachProduct => {
                        if (eachProduct.categoryId === eachCategory.categoryId){
                            relatedBrandIds.push(eachProduct.brandId)
                        }
                    })
                    if (categoryToBrandMap[eachCategory.categoryId]){
                        categoryToBrandMap[eachCategory.categoryId] = [...categoryToBrandMap[eachCategory.categoryId], ...relatedBrandIds]
                    }else {
                        categoryToBrandMap[eachCategory.categoryId] = [...relatedBrandIds]
                    }
                    relatedCategories.push(eachCategory)
                }
            })
            relatedCategories = sortArrayOfObjectsInAlphabeticalOrder(relatedCategories, "categoryName")

            setCategories(relatedCategories)
            setCategoryToBrandMap(categoryToBrandMap)

            let relatedBrands = []
            materialBrands.map(eachBrand => {
                if (brandIds.includes(eachBrand.brandId)) {
                    let relatedCategoryIds = []
                    modifiedConsumerItems.map(eachProduct => {
                        if (eachProduct.brandId === eachBrand.brandId){
                            relatedCategoryIds.push(eachProduct.categoryId)
                        }
                    })
                    if (brandToCategoryMap[eachBrand.brandId]){
                        brandToCategoryMap[eachBrand.brandId] = [...brandToCategoryMap[eachBrand.categoryId], ...relatedCategoryIds]
                    }else {
                        brandToCategoryMap[eachBrand.brandId] = [...relatedCategoryIds]
                    }
                    relatedBrands.push(eachBrand)
                }
            })
            relatedBrands = sortArrayOfObjectsInAlphabeticalOrder(relatedBrands, "brandName")

            setBrands(relatedBrands)
            setBrandToCategoryMap(brandToCategoryMap)
        }
        setIsLoaderVisible(false)
    }

    const fetchShippingAddresses = async () => {
        const shippingAddressessResponse = await getCompanyBranchShipAddrs({branchIds:[currentUserRole.branchId], statusIds:[1], paginator: false}) // {branchIds: [branchId]}
        console.log("shippingAddressessResponse", shippingAddressessResponse)
        if(shippingAddressessResponse.failure){
            
        } else {
            setShippingAddresses(shippingAddressessResponse.data.data)
        }
    }

    const fetchConsumerCartItems = async () => {
        const consumerCartItemsResponse = await getConsumerCartItemsByUserId(userDetails.userId)
        console.log("consumerCartItemsResponse", consumerCartItemsResponse)
        if(consumerCartItemsResponse.failure){

        } else {
            const consumerCartItems = consumerCartItemsResponse.data
            setCartItems(consumerCartItems)
        }
    }

    const handleUpdateConsumerCart = useCallback(_.debounce(async function(updatedCartItems) {
        const consumerCartItemsResponse = await updateConsumerCart(userDetails.userId, updatedCartItems)
        if(consumerCartItemsResponse.failure){

        } else {
            const consumerCartItems = consumerCartItemsResponse.data
            setCartItems(consumerCartItems)
        }
    }, 2000), [])

    const handleQuantityChange = (event, selectedItem) => {
        cartItems.map(eachCartItem => {
            if(eachCartItem.buyerPOItemId === selectedItem.buyerPOItemId){
                eachCartItem.quantity = event.target.value
            }
        })
        setCartItems([...cartItems])
    }

    const updateCartItems = (updateType, selectedItem) => {
        const relatedConsumerItem = products.find(eachProduct => eachProduct.buyerPOItemId === selectedItem?.buyerPOItemId)
        let updatedCartItems = []

        if(updateType === "CLEAR"){
            updatedCartItems = cartItems.map(eachCartItem => {
                return {...eachCartItem, statusId: 2}
            })
        }
        else if(updateType === "ADD"){
            updatedCartItems = [...cartItems, {...selectedItem, quantity: 1, statusId: 1}]
        }
        else if(updateType === "CHANGE"){
            updatedCartItems = cartItems.map(eachCartItem => {
                if(eachCartItem.buyerPOItemId === selectedItem.buyerPOItemId){
                    return {...selectedItem}
                }
                return eachCartItem
            })
        }
        else if(updateType === "INCREASE"){
            updatedCartItems = cartItems.map(eachCartItem => {
                if(eachCartItem.buyerPOItemId === selectedItem.buyerPOItemId){
                    if(eachCartItem.quantity + 1 > relatedConsumerItem.availableQty){
                        eachCartItem.quantity = relatedConsumerItem.availableQty
                        dispatch(raiseToast({ severity: 'warn', detail: `Only ${relatedConsumerItem.availableQty} qty available`, life: 3000 }))
                    } else {
                        eachCartItem.quantity += 1
                    }
                }
                return eachCartItem
            })
        }
        else if(updateType === "DECREASE"){
            updatedCartItems = cartItems.map(eachCartItem => {
                if(eachCartItem.buyerPOItemId === selectedItem.buyerPOItemId){
                    if(eachCartItem.quantity >= 1){
                        eachCartItem.quantity -= 1
                        if(eachCartItem.quantity === 0){
                            eachCartItem.statusId = 2
                        }
                    }
                }
                return eachCartItem
            })
        }
        else if(updateType === "REMOVE"){
            updatedCartItems = cartItems.map(eachCartItem => {
                if(eachCartItem.buyerPOItemId === selectedItem.buyerPOItemId){
                    eachCartItem.statusId = 2
                }
                return eachCartItem
            })
        }
        else if(updateType === "SET"){
            updatedCartItems = cartItems.map(eachCartItem => {
                if(eachCartItem.buyerPOItemId === selectedItem.buyerPOItemId){
                    if(eachCartItem.quantity && eachCartItem.quantity !== "" && parseInt(eachCartItem.quantity) !== 0){
                        if(parseFloat(eachCartItem.quantity) > relatedConsumerItem.availableQty){
                            eachCartItem.quantity = relatedConsumerItem.availableQty
                            dispatch(raiseToast({ severity: 'warn', detail: `Only ${relatedConsumerItem.availableQty} qty available`, life: 3000 }))
                            return eachCartItem
                        }
                        eachCartItem.quantity = parseFloat(eachCartItem.quantity)
                        return eachCartItem
                    } else {
                        eachCartItem.quantity = 1
                    }
                }
                return eachCartItem
            })
        }

        setCartItems(updatedCartItems)
        handleUpdateConsumerCart(updatedCartItems)
    }

    const handleSubmit = async (remarks) => {
        const activeCartItems = cartItems.filter(eachCartItem => eachCartItem.statusId === 1)
        if (activeCartItems.length === 0) {
            return dispatch(raiseToast({ severity: 'error', detail: "Add products to cart", life: 3000 }))
        }
        if(!selectedShipAddrId){
            return dispatch(raiseToast({ severity: 'error', detail: "Please select shipping address", life: 3000 }))
        }

        if (window.confirm("Confirm to place order")) {
            setIsLoaderVisible(true)
            try {
                const modifiedCartItems = cartItems.map(eachCartItem => {
                    const relatedConsumerItem = products.find(eachProduct => eachProduct.buyerPOItemId === eachCartItem.buyerPOItemId)
                    eachCartItem.buyerPOId = relatedConsumerItem.buyerPOId
                    return eachCartItem
                })

                const data = { companyId: currentUserRole.companyId, cartItems: modifiedCartItems, remarks, shipAddrId: selectedShipAddrId }
                const orderResponse = await placeConsumerOrder(data)
                if(orderResponse.failure){
                    dispatch(raiseToast({ severity: 'warn', detail: orderResponse.message, life: 3000 }))
                } else {
                    setCartItems([])
                    fetchConsumerProducts()
                    setVisibleCheckout(false)
                    dispatch(raiseToast({ severity: 'success', detail: "Order placed successfully", life: 3000 }))
                }
                setIsLoaderVisible(false)
            } catch (error) {
                console.error('Error:', error)
                setIsLoaderVisible(false)
            }
        }
    }

    const itemTemplate = (product) => {
        return (
            <Product 
                product={product} 
                handleQuantityChange={handleQuantityChange} 
                updateCartItems={updateCartItems} 
                materialBrands={materialBrands}
                materialCategories={materialCategories}
            />
        )
    }

    const renderProductsSkeleton = () => {
        return (
            <div className="flex" style={{flexWrap: "wrap", justifyContent: "space-evenly"}} >
                <ProductItemSkeleton />
                <ProductItemSkeleton />
                <ProductItemSkeleton />
                <ProductItemSkeleton />
                <ProductItemSkeleton />
                <ProductItemSkeleton />
                <ProductItemSkeleton />
                <ProductItemSkeleton />
                <ProductItemSkeleton />
                <ProductItemSkeleton />
                <ProductItemSkeleton />
                <ProductItemSkeleton />
            </div>
        )
    }

    const renderFilterSkeleton = () => {
        return (
            <div className="flex" style={{flexWrap: "wrap", justifyContent: "space-evenly"}} >
                <Skeleton width="10rem" className="mb-2" />
                <Skeleton width="10rem" className="mb-2" />
                <Skeleton width="10rem" className="mb-2" />
                <Skeleton width="10rem" className="mb-2" />
                <Skeleton width="10rem" className="mb-2" />
                <Skeleton width="10rem" className="mb-2" />
                <Skeleton width="10rem" className="mb-2" />
            </div>
        )
    }

    const activeCartItems = cartItems.filter(eachCartItem => eachCartItem.statusId === 1)

    const displayProducts = selectedProducts.map(eachProduct => {
        const relatedCartItem = activeCartItems.find(prod => {
            return prod.buyerPOItemId == eachProduct.buyerPOItemId
        })
        return {
            eachProduct: eachProduct,
            relatedCartItem: relatedCartItem
        }
    })


    let selectedCategories = []
    let unSelectedCategories = []

    categories.map(eachCategory => {
        let relatedProducts = products.filter(eachProduct => {
            if (eachCategory.categoryId === eachProduct.categoryId){
                if (selectedBrandIds.length){
                    return selectedBrandIds.includes(eachProduct.brandId)
                }
                return eachProduct
            }
        })
        if (selectedCategoryIds.includes(eachCategory.categoryId)){
            selectedCategories.push({...eachCategory, totalItems: relatedProducts.length})
        }
        if (!selectedCategoryIds.includes(eachCategory.categoryId)){
            unSelectedCategories.push({...eachCategory, totalItems: relatedProducts.length})
        }
    })

    let updatedCategories = [...selectedCategories, ...unSelectedCategories]

    if (selectedBrandIds.length){
        let requiredCategoryIds = []
        selectedBrandIds.map(eachBrandId => {
            let relatedCategoryIds = brandToCategoryMap[eachBrandId]
            requiredCategoryIds.push(...relatedCategoryIds)
        })
        updatedCategories = updatedCategories.filter(eachCategory => requiredCategoryIds.includes(eachCategory.categoryId))
    }
    updatedCategories = updatedCategories.filter(eachCategory => eachCategory.totalItems > 0)

    let selectedBrands = []
    let unSelectedBrands = []

    brands.map(eachBrand => {
        let relatedProducts = products.filter(eachProduct => {
            if (eachProduct.brandId === eachBrand.brandId){
                if (selectedCategoryIds.length){
                    return selectedCategoryIds.includes(eachProduct.categoryId)
                }

                return eachProduct
            }
        })
        if (selectedBrandIds.includes(eachBrand.brandId)){
            selectedBrands.push({...eachBrand, totalItems: relatedProducts.length})
        }
        if (!selectedBrandIds.includes(eachBrand.brandId)){
            unSelectedBrands.push({...eachBrand, totalItems: relatedProducts.length})
        }
    })

    let updatedBrands = [...selectedBrands, ...unSelectedBrands]

    if (selectedCategoryIds.length){
        let requiredBrandIds = []
        selectedCategoryIds.map(eachCategoryId => {
            let relatedBrandIds = categoryToBrandMap[eachCategoryId]
            requiredBrandIds.push(...relatedBrandIds)
        })
        updatedBrands = updatedBrands.filter(eachBrand => requiredBrandIds.includes(eachBrand.brandId))
    }
    updatedBrands = updatedBrands.filter(eachBrand => eachBrand.totalItems > 0)

    if (!showMore){
        updatedBrands = updatedBrands.slice(0, 20)
    }

    return (
        <>
            <div className="catalogue-request-layout" style={{padding: "0 6%"}}>
                <div className="h-3rem flex md:flex-row flex-column justify-content-between mb-5 mt-2">
                    <div>
                        <h4 className="m-2"></h4>
                    </div>
                    <div className="catalogue-request-search md:w-5 p-input-icon-right">
                        <div className="flex flex-row justify-content-center align-items-center" style={{position: "absolute", width: "50px", height: "45px", right: 0, backgroundColor: "#ffa53b", borderTopRightRadius: "6px", borderBottomRightRadius: "6px"}}>
                            <i className="pi pi-search" style={{color: "white", fontSize: "17px", marginTop: "3px"}} />
                        </div>
                        <InputText placeholder="I'm searching for..." value={searchText} style={{ width: "100%", height: "45px", fontSize: "16px", borderColor: "#ececec" }} onChange={(e) => setSearchText(e.target.value)} />
                    </div>
                    <div className="flex justify-content-end mt-2 md:mt-0">
                        <div className="right-side-menu flex mt-2">
                            <div className="right-side">
                                <div className="onhover-dropdown header-badge">
                                    <button label={`VIEW CART (${(cartItems.length)})`} onClick={() => setVisibleCheckout(true)} type="button" className="btn p-0 position-relative header-wishlist">
                                        <i className="pi pi-shopping-cart" style={{ fontSize: '1.5rem' }}></i>
                                        <div className="cart-count">
                                            {activeCartItems.length}
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="catalogue-request-container flex md:flex-row flex-column">
                    <div className="accordion custom-accordion mt-4 col-12 md:col-2 categories-and-brand-container" style={{ paddingRight: "20px" }} id="accordionExample">
                        <div className="accordion-item flex flex-row md: flex-column" style={{ justifyContent: "left" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "11px" }}>
                                <h2 className="accordion-header" id="panelsStayOpen-headingOne" style={{ color: "#222", fontSize: "18px", borderBottom: "2px solid #0da487", paddingBottom: "6px" }}>
                                    Categories
                                </h2>
                                <i className="pi pi-angle-down" style={{ fontSize: '1rem' }}></i>
                            </div>

                            <div id="collapseOne" className="accordion-collapse collapse show">
                                <div className="accordion-body">
                                    <div className="form-floating theme-form-floating-2 search-box" style={{ position: "relative", display: "flex", justifyContent: "center" }}>
                                        <input style={{ width: "100%", border: "1px solid #ced4da", borderRadius: "0.25rem" }} type="search" className="form-control" id="search"
                                            placeholder="Search" value={searchCategoryText} onChange={(e) => setSearchCategoryText(e.target.value)}/>

                                        <div style={{ position: "absolute", left: "86%", top: "0.9rem" }}>
                                            <i className="pi pi-search" style={{ fontSize: '1rem' }}></i> 
                                        </div>
                                    </div>
                                    {
                                        selectedCategoryIds.length ? <div className="mt-3" style={{cursor: 'pointer', textDecoration: 'underline', fontSize: "14px"}} onClick={() => setSelectedCategoryIds([])}>Clear All</div> : null
                                    }
                                    <ul className="category-list custom-padding custom-height pt-1">
                                        {
                                            isLoaderVisible ? 
                                            renderFilterSkeleton()
                                            :
                                            updatedCategories.map((category) => {
                                                return (
                                                    <li style={{marginBottom: "0.2rem"}}>
                                                        <div className="form-check ps-0 m-0 category-list-box">
                                                            <input className="checkbox_animated" type="checkbox" id="fruit" onClick={() => handleSelectCategory(category.categoryId)} checked={selectedCategoryIds.includes(category.categoryId)}/>
                                                            <label className="form-check-label" htmlFor="fruit">
                                                                <span className="name">{category.categoryName}</span>
                                                                <span className="number">({category.totalItems})</span>
                                                            </label>
                                                        </div>
                                                    </li>
                                                )
                                            })
                                        }
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="accordion-item mt-5" style={{ display: "flex", flexDirection: "column", justifyContent: "left"}}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "11px" }}>
                                <h2 className="accordion-header" id="panelsStayOpen-headingOne" style={{ color: "#222", fontSize: "18px", borderBottom: "2px solid #0da487", paddingBottom: "6px" }}>
                                    Brands
                                </h2>
                                <i className="pi pi-angle-down" style={{ fontSize: '1rem' }}></i> 
                            </div>

                            <div id="collapseOne" className="accordion-collapse collapse show">
                                <div className="accordion-body">
                                    <div className="form-floating theme-form-floating-2 search-box" style={{ position: "relative", display: "flex", justifyContent: "center" }}>
                                        <input style={{ width: "100%", border: "1px solid #ced4da", borderRadius: "0.25rem" }} type="search" className="form-control" id="search"
                                            placeholder="Search" value={searchBrandText} onChange={(e) => setSearchBrandText(e.target.value)}/>

                                        <div style={{ position: "absolute", left: "86%", top: "0.9rem" }}>
                                            <i className="pi pi-search" style={{ fontSize: '1rem' }}></i> 
                                        </div>
                                    </div>
                                    {
                                        selectedBrandIds.length ? <div className="mt-3" style={{cursor: 'pointer', textDecoration: 'underline', fontSize: "14px"}} onClick={() => setSelectedBrandIds([])}>Clear All</div> : null
                                    }
                                    <ul className="category-list custom-padding custom-height pt-1">
                                        {
                                            isLoaderVisible ?
                                                renderFilterSkeleton()
                                                :
                                                updatedBrands.map((brand) => {
                                                    return (
                                                        <li style={{ marginBottom: '0.2rem' }}>
                                                            <div className="form-check ps-0 m-0 category-list-box">
                                                                <input className="checkbox_animated" type="checkbox" id="fruit" onClick={() => handleSelectBrand(brand.brandId)} checked={selectedBrandIds.includes(brand.brandId)} />
                                                                <label className="form-check-label" htmlFor="fruit">
                                                                    <span className="name">{brand.brandName}</span>
                                                                    <span className="number">({brand.totalItems})</span>
                                                                </label>
                                                            </div>
                                                        </li>
                                                    )
                                                })
                                        }
                                    </ul>
                                    {/* {
                                        showMore ?
                                        <div className="flex flex-row" style={{cursor: 'pointer'}} onClick={() => setShowMore(false)}>
                                            <i className="pi pi-angle-up mt-3"/>
                                            <p className="ml-2">Show Less</p>
                                        </div>
                                        :
                                        <div className="flex flex-row" style={{cursor: 'pointer'}} onClick={() => setShowMore(true)}>
                                            <i className="pi pi-angle-down mt-3"/>
                                            <p className="ml-2">Show More</p>
                                        </div>
                                    } */}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="catalogue-card-container consumer-req-product-container p-0 col-12 md:col-10 mt-3 md:mt-0" style={{}}>
                        <div className="catalogue-request buyer-portal-card-container flex flex-wrap ml-3">
                            {
                                isLoaderVisible ? 
                                    renderProductsSkeleton()
                                :
                                <DataView 
                                    value={displayProducts} 
                                    itemTemplate={itemTemplate} 
                                    paginator 
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    rows={20} 
                                    className="consumer-req-data-view"
                                />
                            }
                        </div>
                        <div className="">
                            <Dialog headerStyle={{ padding: "2px" }} showHeader={false} onHide={() => setVisibleCheckout(false)} className="catalogue-request--cart-dialog custom-scrollbar" header="" visible={visibleCheckout} style={{ width: '80vw', minHeight: '40vh' }} >
                                <Checkout
                                    setVisibleCheckout={setVisibleCheckout}
                                    products={products}
                                    cartItems={activeCartItems}
                                    remarks={remarks}
                                    setRemarks={setRemarks}
                                    updateCartItems={updateCartItems}
                                    handleSubmit={handleSubmit}
                                    shippingAddresses={shippingAddresses}
                                    selectedShipAddrId={selectedShipAddrId}
                                    setSelectedShipAddrId={setSelectedShipAddrId}
                                />
                            </Dialog>
                        </div>
                        <Loading visible={isLoaderVisible} />
                    </div>
                </div>
            </div>
        </>
    )
}

export default ConsumerRequest